/**
 * Row-change domain event schemas.
 *
 * Typed payload validation for Mycelium events emitted by the server-side
 * Realtime ↔ Mycelium bridge (Postgres triggers on high-value tables publishing
 * through `mycelium_emit_row_change()` → PGMQ).
 *
 * Each event type follows the pattern `{entity}_{op}` where op ∈ {created,
 * updated, deleted}. Payload shape is uniform across entities so downstream
 * connectors can handle row-change events generically. Snake_case event types
 * keep the Mycelium integrity scanner happy.
 *
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module row-change.events
 * @since 2026-04-17
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Shared payload schema ───────────────────────────────────────────────

/**
 * Uniform payload for every row-change event.
 *
 * `op` is the SQL operation (`insert`|`update`|`delete`).
 * `table` is the `public.<name>` source table.
 * `row` is the primary row snapshot (NEW for insert/update, OLD for delete).
 * `previous` is the OLD snapshot (update/delete only).
 * `dedupKey` mirrors the dedup key used by the worker (hash of
 * `table|op|pk|updated_at`) so subscribers can short-circuit duplicates.
 */
export const RowChangePayload = z.object({
  op: z.enum(['insert', 'update', 'delete']),
  table: z.string(),
  id: z.string().optional(),
  organizationId: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  row: z.record(z.unknown()).optional(),
  previous: z.record(z.unknown()).optional(),
  changedColumns: z.array(z.string()).optional(),
  dedupKey: z.string().optional(),
  emittedAt: z.string().optional(),
  source: z.string(),
})

export type RowChangePayload = z.infer<typeof RowChangePayload>

// ── Helpers ─────────────────────────────────────────────────────────────

const rowChangeSchema = (domain: string, type: string) =>
  BackboneEventSchema.extend({
    domain: z.literal(domain as never),
    type: z.literal(type as never),
    payload: RowChangePayload,
  })

// ── Task domain (todos, projects) ───────────────────────────────────────

export const TaskCreatedEvent = rowChangeSchema('task', 'task_created')
export const TaskUpdatedEvent = rowChangeSchema('task', 'task_updated')
export const TaskDeletedEvent = rowChangeSchema('task', 'task_deleted')

export const ProjectCreatedEvent = rowChangeSchema('task', 'project_created')
export const ProjectUpdatedEvent = rowChangeSchema('task', 'project_updated')
export const ProjectDeletedEvent = rowChangeSchema('task', 'project_deleted')

// ── Notification domain ─────────────────────────────────────────────────

export const NotificationCreatedEvent = rowChangeSchema('notification', 'notification_created')
export const NotificationUpdatedEvent = rowChangeSchema('notification', 'notification_updated')
export const NotificationDeletedEvent = rowChangeSchema('notification', 'notification_deleted')

// ── CRM domain (clients) ────────────────────────────────────────────────

export const ClientCreatedEvent = rowChangeSchema('crm', 'client_created')
export const ClientUpdatedEvent = rowChangeSchema('crm', 'client_updated')
export const ClientDeletedEvent = rowChangeSchema('crm', 'client_deleted')

// ── Accounting domain (invoices) ────────────────────────────────────────

export const InvoiceCreatedEvent = rowChangeSchema('accounting', 'invoice_created')
export const InvoiceUpdatedEvent = rowChangeSchema('accounting', 'invoice_updated')
export const InvoiceDeletedEvent = rowChangeSchema('accounting', 'invoice_deleted')

// ── AI domain (cortex_messages) ─────────────────────────────────────────

export const CortexMessageCreatedEvent = rowChangeSchema('ai', 'cortex_message_created')
export const CortexMessageUpdatedEvent = rowChangeSchema('ai', 'cortex_message_updated')
export const CortexMessageDeletedEvent = rowChangeSchema('ai', 'cortex_message_deleted')

// ── System domain (bulk aggregation) ────────────────────────────────────

/**
 * Emitted instead of per-row events when a single transaction mutates more
 * rows than the per-txn threshold (default 100). Prevents migration/backfill
 * operations from flooding `mycelium_events`.
 */
export const BulkRowChangeEvent = BackboneEventSchema.extend({
  domain: z.literal('system'),
  type: z.literal('bulk_row_change'),
  payload: z.object({
    table: z.string(),
    op: z.enum(['insert', 'update', 'delete']),
    count: z.number(),
    organizationId: z.string().nullable().optional(),
    source: z.string(),
    txnStart: z.string().optional(),
  }),
})

// ── All row-change events ───────────────────────────────────────────────

export const RowChangeDomainEvents = [
  TaskCreatedEvent,
  TaskUpdatedEvent,
  TaskDeletedEvent,
  ProjectCreatedEvent,
  ProjectUpdatedEvent,
  ProjectDeletedEvent,
  NotificationCreatedEvent,
  NotificationUpdatedEvent,
  NotificationDeletedEvent,
  ClientCreatedEvent,
  ClientUpdatedEvent,
  ClientDeletedEvent,
  InvoiceCreatedEvent,
  InvoiceUpdatedEvent,
  InvoiceDeletedEvent,
  CortexMessageCreatedEvent,
  CortexMessageUpdatedEvent,
  CortexMessageDeletedEvent,
  BulkRowChangeEvent,
] as const

/**
 * Canonical list of row-change event type strings that the database-change
 * bridge may emit. Consumed by the `DatabaseChangeConnector` for subscription
 * filtering and by downstream services that want to detect row-change events.
 */
export const ROW_CHANGE_EVENT_TYPES = [
  'task_created',
  'task_updated',
  'task_deleted',
  'project_created',
  'project_updated',
  'project_deleted',
  'notification_created',
  'notification_updated',
  'notification_deleted',
  'client_created',
  'client_updated',
  'client_deleted',
  'invoice_created',
  'invoice_updated',
  'invoice_deleted',
  'cortex_message_created',
  'cortex_message_updated',
  'cortex_message_deleted',
  'bulk_row_change',
] as const

export type RowChangeEventType = typeof ROW_CHANGE_EVENT_TYPES[number]
